import React, { useState, useRef, useEffect } from 'react';
import './calendar.scss';

interface CalendarProps {
  isOpen: boolean;
  onClose: () => void;
  onDateSelect: (startDate: Date, endDate?: Date, selectedDates?: Date[]) => void;
  selectedDates?: { start: Date; end?: Date };
  initialSelectedDates?: Date[]; // For multiple selection mode
  allowRange?: boolean;
  allowMultiple?: boolean;
  inline?: boolean;
}

const Calendar: React.FC<CalendarProps> = ({
  isOpen,
  onClose,
  onDateSelect,
  selectedDates,
  initialSelectedDates = [],
  allowRange = true,
  allowMultiple = false,
  inline = false
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedRange, setSelectedRange] = useState<{ start: Date | null; end: Date | null }>({
    start: selectedDates?.start || null,
    end: selectedDates?.end || null
  });
  const [multipleSelectedDates, setMultipleSelectedDates] = useState<Date[]>(initialSelectedDates);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [lastClickedDate, setLastClickedDate] = useState<Date | null>(null); // Track the currently active date
  const calendarRef = useRef<HTMLDivElement>(null);
  const prevInitialDatesRef = useRef<string>('');
  
  // Drag selection state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartDate, setDragStartDate] = useState<Date | null>(null);
  const [dragEndDate, setDragEndDate] = useState<Date | null>(null);
  const [wasDragging, setWasDragging] = useState(false);
  
  // Use refs to track current drag dates (to avoid stale closure in event listeners)
  const dragStartRef = useRef<Date | null>(null);
  const dragEndRef = useRef<Date | null>(null);
  const isDraggingRef = useRef<boolean>(false);

  // Update multipleSelectedDates when initialSelectedDates changes
  useEffect(() => {
    if (allowMultiple) {
      // Convert to JSON strings for deep comparison
      const newDatesStr = JSON.stringify(initialSelectedDates.map(d => d.toISOString()));
      const currentDatesStr = JSON.stringify(multipleSelectedDates.map(d => d.toISOString()));
      
      // Only update if the dates actually changed AND are different from current state
      if (prevInitialDatesRef.current !== newDatesStr && newDatesStr !== currentDatesStr) {
        // Update internal state to match parent
        setMultipleSelectedDates(initialSelectedDates);
        prevInitialDatesRef.current = newDatesStr;
      }
    }
  }, [initialSelectedDates, allowMultiple, multipleSelectedDates]);

  // Month names
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // Month grid layout (3x4)
  const monthGrid = [
    ['Jan', 'Feb', 'Mar', 'Apr'],
    ['May', 'Jun', 'Jul', 'Aug'],
    ['Sep', 'Oct', 'Nov', 'Dec']
  ];

  // Day names
  const dayNames = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  // Get calendar days for current month
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the day of week for first day (0 = Sunday, 1 = Monday, etc.)
    // Convert to Monday = 0, Sunday = 6
    const firstDayOfWeek = (firstDay.getDay() + 6) % 7;
    
    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDayOfWeek; i++) {
      const prevDate = new Date(year, month, -(firstDayOfWeek - 1 - i));
      days.push(prevDate);
    }
    
    // Add all days of the current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    
    // Add days from next month to fill the last week
    const remainingCells = 42 - days.length; // 6 weeks * 7 days = 42
    for (let day = 1; day <= remainingCells; day++) {
      days.push(new Date(year, month + 1, day));
    }
    
    return days;
  };

  // Handle date click
  const handleDateClick = (date: Date) => {
    // Prevent click if we just finished dragging
    if (wasDragging) {
      setWasDragging(false);
      return;
    }
    
    if (allowMultiple) {
      // Multiple date selection - only toggle individual dates, no range filling
      const dateString = date.toDateString();
      const isAlreadySelected = multipleSelectedDates.some(d => d.toDateString() === dateString);
      
      let newSelectedDates: Date[];
      if (isAlreadySelected) {
        // Remove date if already selected
        newSelectedDates = multipleSelectedDates.filter(d => d.toDateString() !== dateString);
        // Clear last clicked date if removing it
        if (lastClickedDate && lastClickedDate.toDateString() === dateString) {
          setLastClickedDate(null);
        }
      } else {
        // Add only this specific date to selection (no range filling)
        newSelectedDates = [...multipleSelectedDates, new Date(date)];
        // Set as the currently active date
        setLastClickedDate(new Date(date));
      }
      setMultipleSelectedDates(newSelectedDates);
      
      // Update the ref to prevent unnecessary sync from parent
      prevInitialDatesRef.current = JSON.stringify(newSelectedDates.map(d => d.toISOString()));
      
      onDateSelect(date, undefined, newSelectedDates);
      return;
    }

    if (!allowRange) {
      // Single date selection
      setSelectedRange({ start: date, end: null });
      onDateSelect(date);
      onClose();
      return;
    }

    // Range selection
    if (!selectedRange.start || (selectedRange.start && selectedRange.end)) {
      // Start new selection
      setSelectedRange({ start: date, end: null });
    } else if (selectedRange.start && !selectedRange.end) {
      // Complete the range
      const start = selectedRange.start;
      const end = date;
      
      if (start <= end) {
        setSelectedRange({ start, end });
        onDateSelect(start, end);
      } else {
        setSelectedRange({ start: end, end: start });
        onDateSelect(end, start);
      }
    }
  };

  // Helper function to get all dates between two dates (inclusive)
  const getDatesBetween = (start: Date, end: Date): Date[] => {
    const dates: Date[] = [];
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    // Ensure start is before end
    const [first, last] = startDate <= endDate ? [startDate, endDate] : [endDate, startDate];
    
    const currentDate = new Date(first);
    while (currentDate <= last) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  // Handle mouse down on date (start drag)
  const handleMouseDown = (date: Date, event: React.MouseEvent) => {
    if (allowMultiple && isCurrentMonth(date)) {
      event.preventDefault(); // Prevent text selection
      setIsDragging(true);
      setDragStartDate(date);
      setDragEndDate(date);
      
      // Update refs for event listeners
      isDraggingRef.current = true;
      dragStartRef.current = date;
      dragEndRef.current = date;
    }
  };

  // Handle mouse enter on date (during drag)
  const handleMouseEnter = (date: Date) => {
    if (isDragging && allowMultiple && isCurrentMonth(date)) {
      setDragEndDate(date);
      dragEndRef.current = date; // Update ref
    } else if (isCurrentMonth(date)) {
      setHoverDate(date);
    }
  };

  // Handle mouse up (end drag)
  const handleMouseUp = () => {
    // Use refs to get current values (avoid stale closure)
    const currentDragStart = dragStartRef.current;
    const currentDragEnd = dragEndRef.current;
    const currentIsDragging = isDraggingRef.current;
    
    
    if (currentIsDragging && currentDragStart && currentDragEnd) {
      // Check if user actually dragged (not just a click)
      const hasDragged = currentDragStart.toDateString() !== currentDragEnd.toDateString();
      
      if (hasDragged) {
        // Get all dates in the dragged range
        const datesInRange = getDatesBetween(currentDragStart, currentDragEnd);
        
        // Filter to only include dates from current month
        const validDates = datesInRange.filter(d => isCurrentMonth(d));
        
        // Add these dates to selection (merge with existing)
        const existingDateStrings = multipleSelectedDates.map(d => d.toDateString());
        const newDates = validDates.filter(d => !existingDateStrings.includes(d.toDateString()));
        const updatedDates = [...multipleSelectedDates, ...newDates];
        
        setMultipleSelectedDates(updatedDates);
        
        // Set the last date in drag range as the currently active date
        setLastClickedDate(new Date(currentDragEnd));
        
        // Update the ref to prevent unnecessary sync from parent
        prevInitialDatesRef.current = JSON.stringify(updatedDates.map(d => d.toISOString()));
        
        onDateSelect(currentDragStart, undefined, updatedDates);
        
        // Mark that we were dragging to prevent click event
        setWasDragging(true);
        // Reset after a short delay
        setTimeout(() => setWasDragging(false), 100);
      }
      
      // Reset drag state
      setIsDragging(false);
      setDragStartDate(null);
      setDragEndDate(null);
      isDraggingRef.current = false;
      dragStartRef.current = null;
      dragEndRef.current = null;
    }
  };

  // Check if date is in current month
  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentMonth.getMonth() && 
           date.getFullYear() === currentMonth.getFullYear();
  };

  // Check if date is selected
  const isSelected = (date: Date) => {
    if (allowMultiple) {
      // Check if date is in the already selected dates
      const isInSelectedDates = multipleSelectedDates.some(d => d.toDateString() === date.toDateString());
      
      // Also check if date is in the current drag range
      if (isDragging && dragStartDate && dragEndDate) {
        const dragDates = getDatesBetween(dragStartDate, dragEndDate);
        const isInDragRange = dragDates.some(d => d.toDateString() === date.toDateString());
        return isInSelectedDates || isInDragRange;
      }
      
      return isInSelectedDates;
    }

    if (!selectedRange.start) return false;
    
    if (!selectedRange.end) {
      return date.toDateString() === selectedRange.start.toDateString();
    }
    
    return date >= selectedRange.start && date <= selectedRange.end;
  };

  // Check if date is in range (for hover effect)
  const isInRange = (date: Date) => {
    if (!allowRange || !selectedRange.start || selectedRange.end) return false;
    if (!hoverDate) return false;
    
    const start = selectedRange.start;
    const end = hoverDate;
    
    if (start <= end) {
      return date >= start && date <= end;
    } else {
      return date >= end && date <= start;
    }
  };

  // Check if date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  // Navigate years in month selector
  const goToPreviousYear = () => {
    setCurrentYear(currentYear - 1);
  };

  const goToNextYear = () => {
    setCurrentYear(currentYear + 1);
  };

  // Handle month selector toggle
  const toggleMonthSelector = () => {
    setShowMonthSelector(!showMonthSelector);
    setCurrentYear(currentMonth.getFullYear());
  };

  // Handle month selection
  const handleMonthSelect = (monthIndex: number) => {
    const newDate = new Date(currentYear, monthIndex, 1);
    setCurrentMonth(newDate);
    setShowMonthSelector(false);
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key to close month selector
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showMonthSelector) {
          setShowMonthSelector(false);
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, showMonthSelector, onClose]);

  // Handle global mouse up for drag selection
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp();
      }
    };

    if (isDragging) {
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging]);

  if (!isOpen) return null;

  const calendarDays = getCalendarDays();

  const inner = (
    <div className="calendar-container" ref={calendarRef}>
      {/* Header */}
      <div className="calendar-header">
        {!showMonthSelector ? (
          <>
            <button className="month-selector" onClick={toggleMonthSelector}>
              <span className="month-year">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </span>
              <svg className="dropdown-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            <div className="navigation-buttons">
              <button className="nav-btn" onClick={goToPreviousMonth}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className="nav-btn" onClick={goToNextMonth}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="year-selector">
              <span className="year-text">{currentYear}</span>
              <svg className="dropdown-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            <div className="navigation-buttons">
              <button className="nav-btn" onClick={goToPreviousYear}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className="nav-btn" onClick={goToNextYear}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Content Area */}
      {!showMonthSelector ? (
        /* Days Grid */
        <div className="calendar-days">
          {/* Day Headers */}
          <div className="day-headers">
            {dayNames.map(day => (
              <div key={day} className="day-header">
                <span>{day}</span>
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="calendar-grid">
            {calendarDays.map((date, index) => {
              if (!date) return <div key={index} className="calendar-cell empty"></div>;
              
              const isCurrentMonthDate = isCurrentMonth(date);
              const isSelectedDate = isSelected(date);
              const isInRangeDate = isInRange(date);
              const isTodayDate = isToday(date);
              const isActiveDate = lastClickedDate && date.toDateString() === lastClickedDate.toDateString();
              
              return (
                <div
                  key={index}
                  className={`calendar-cell ${!isCurrentMonthDate ? 'other-month' : ''} ${isSelectedDate ? 'selected' : ''} ${isActiveDate ? 'active-selected' : ''} ${isInRangeDate ? 'in-range' : ''} ${isTodayDate ? 'today' : ''} ${isDragging ? 'dragging' : ''}`}
                  onClick={() => isCurrentMonthDate && handleDateClick(date)}
                  onMouseDown={(e) => handleMouseDown(date, e)}
                  onMouseEnter={() => handleMouseEnter(date)}
                  onMouseLeave={() => setHoverDate(null)}
                  style={{ userSelect: 'none', cursor: isDragging ? 'grabbing' : 'pointer' }}
                >
                  <span className="date-number">{date.getDate()}</span>
                  {isTodayDate && <div className="today-indicator"></div>}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Month Selector Grid */
        <div className="month-selector-grid">
          <div className="month-selector-header">
            <button className="close-month-selector" onClick={() => setShowMonthSelector(false)}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          {monthGrid.map((row, rowIndex) => (
            <div key={rowIndex} className="month-row">
              {row.map((month, colIndex) => {
                const monthIndex = rowIndex * 4 + colIndex;
                const isCurrentMonth = monthIndex === currentMonth.getMonth() && currentYear === currentMonth.getFullYear();
                
                return (
                  <button
                    key={month}
                    className={`month-cell ${isCurrentMonth ? 'selected' : ''}`}
                    onClick={() => handleMonthSelect(monthIndex)}
                  >
                    <span>{month}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Footer for multiple selection */}
      {allowMultiple && (
        <div className="calendar-footer">
          <div className="footer-left">
            <div className="selected-count">
              {multipleSelectedDates.length} date{multipleSelectedDates.length !== 1 ? 's' : ''} selected
            </div>
            {multipleSelectedDates.length > 0 && (
              <button 
                className="clear-btn" 
                onClick={() => {
                  setMultipleSelectedDates([]);
                  setLastClickedDate(null);
                  onDateSelect(new Date(), undefined, []);
                }}
              >
                Clear All
              </button>
            )}
          </div>
          <button className="done-btn" onClick={onClose}>
            Done
          </button>
        </div>
      )}
    </div>
  );

  // Return inline or overlay based on prop
  return inline ? (
    <div className="calendar-inline">
      {inner}
    </div>
  ) : (
    <div className="calendar-overlay">
      {inner}
    </div>
  );
};

export default Calendar;